import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
export class DiscountsMonitorInterceptor implements HttpInterceptor {
  private callCounts: { [key: string]: { count: number; lastCall: number; calls: number[] } } = {};
  private readonly WARNING_THRESHOLD = 3; // Advertencia después de 3 llamadas
  private readonly CRITICAL_THRESHOLD = 5; // Crítico después de 5 llamadas
  private readonly TIME_WINDOW = 60000; // 1 minuto en milisegundos
  private readonly MIN_INTERVAL = 5000; // 5 segundos mínimo entre llamadas

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = request.url;
    const now = Date.now();

    // Solo monitorear llamadas a servicios de descuentos
    if (this.shouldMonitor(url)) {
      this.monitorCall(url, now);
    }

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (this.shouldMonitor(url)) {
            console.log(`✅ Descuentos API response received: ${this.getServiceName(url)}`);
          }
        },
        error: (error) => {
          if (this.shouldMonitor(url)) {
            console.error(`❌ Descuentos API error (${this.getServiceName(url)}):`, error);
          }
        }
      })
    );
  }

  private shouldMonitor(url: string): boolean {
    return url.includes('/ipa/beneficios') ||
           url.includes('/ipa/catalogos') ||
           url.includes('/ipa/descuentos');
  }

  private getServiceName(url: string): string {
    if (url.includes('/ipa/beneficios')) return 'Beneficios';
    if (url.includes('/ipa/catalogos')) return 'Catálogos';
    if (url.includes('/ipa/descuentos')) return 'Descuentos Permanentes';
    return 'Desconocido';
  }

  private monitorCall(url: string, now: number): void {
    // Inicializar contador para esta URL si no existe
    if (!this.callCounts[url]) {
      this.callCounts[url] = { count: 0, lastCall: 0, calls: [] };
    }

    const callData = this.callCounts[url];

    // Limpiar llamadas antiguas (más de 1 minuto)
    callData.calls = callData.calls.filter(
      timestamp => now - timestamp < this.TIME_WINDOW
    );

    // Verificar intervalo mínimo entre llamadas
    if (callData.lastCall > 0 && (now - callData.lastCall) < this.MIN_INTERVAL) {
      console.warn(`⚠️ Descuentos API: Llamada demasiado frecuente (${Math.round(now - callData.lastCall)}ms entre llamadas)`);
      console.warn(`   URL: ${url}`);
      console.warn(`   Servicio: ${this.getServiceName(url)}`);
      console.warn(`   Posible problema: Cache no está funcionando correctamente`);
    }

    // Agregar nueva llamada
    callData.calls.push(now);
    callData.count = callData.calls.length;
    callData.lastCall = now;

    // Log de la llamada
    console.log(`🔄 Descuentos API call #${callData.count} (${this.getServiceName(url)}): ${url}`);

    // Verificar umbrales de alerta
    if (callData.count >= this.CRITICAL_THRESHOLD) {
      console.error(`🚨 Descuentos API: CRÍTICO - Demasiadas llamadas HTTP!`);
      console.error(`   URL: ${url}`);
      console.error(`   Servicio: ${this.getServiceName(url)}`);
      console.error(`   Total de llamadas en 1 minuto: ${callData.count}`);
      console.error(`   Posible bucle infinito o problema de cache`);
      console.error(`   Última llamada: ${new Date(callData.lastCall).toLocaleTimeString()}`);
    } else if (callData.count >= this.WARNING_THRESHOLD) {
      console.warn(`⚠️ Descuentos API: Muchas llamadas HTTP detectadas`);
      console.warn(`   URL: ${url}`);
      console.warn(`   Servicio: ${this.getServiceName(url)}`);
      console.warn(`   Total de llamadas en 1 minuto: ${callData.count}`);
      console.warn(`   Considerar optimizar cache o reducir llamadas`);
    }
  }

  // Método para obtener estadísticas (útil para debugging)
  getStats(): { [key: string]: { count: number; lastCall: number; calls: number[] } } {
    return { ...this.callCounts };
  }

  // Método para limpiar estadísticas
  clearStats(): void {
    this.callCounts = {};
  }
}
